/**
 * Password-reset API — TanStack Query mutation factories.
 *
 * Phase-2 Wave-3 W3.T3. The Employees page detail modal uses these to
 * issue an admin-driven reset link and (optionally) re-deliver it by
 * email. Two endpoints, mirrored with the BE contracts:
 *
 *   POST /auth/password-reset/issue       (IssueResetRequest)
 *     → { resetUrl, expiresAt, resetTokenId }
 *
 *   POST /auth/password-reset/send-email  ({ rawToken })
 *     → { ok: boolean, messageId?: string, error?: string }
 *
 * The send-email response is *informational* (per Q64) — the reset link
 * itself was already created by `/issue`, so a transport failure surfaces
 * as `{ ok: false, error: '...' }` with HTTP 200 and the caller decides
 * whether to retry. We invalidate `qk.users.all` on issue so an
 * "Активные / Архив" list view picks up any side effects (e.g. session
 * revocation reflected in `updatedAt`).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { IssueResetRequest, SendResetEmailRequest } from '@aris/shared';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

export interface IssueResetResult {
  resetUrl: string;
  expiresAt: string;
  resetTokenId: string;
}

export function useIssueResetMutation() {
  const qc = useQueryClient();
  return useMutation<IssueResetResult, unknown, IssueResetRequest>({
    mutationFn: (body) =>
      apiFetch<IssueResetResult>('/auth/password-reset/issue', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
    },
  });
}

export interface SendResetEmailResult {
  ok: boolean;
  messageId?: string;
  error?: string;
}

export function useSendResetEmailMutation() {
  return useMutation<SendResetEmailResult, unknown, SendResetEmailRequest>({
    mutationFn: (body) =>
      apiFetch<SendResetEmailResult>('/auth/password-reset/send-email', {
        method: 'POST',
        body,
      }),
  });
}

// ─── Emergency Admin recovery (Supervisor-only) ────────────────────────────

/**
 * Result shape for the supervisor-only "emergency Admin recovery" path —
 * mirrors `IssueResetResult` from the BE (`PasswordResetService.
 * issueEmergencyForAdmin`).
 *
 * Note: `absoluteUrl` is populated when `FE_BASE_URL` is configured on
 * the API. The supervisor UI prefers it over `resetUrl` for the copy
 * panel since the URL is meant to be pasted into a chat / SMS — but
 * still falls back to `toAbsoluteLink(resetUrl)` (which uses
 * `window.location.origin`) when absent.
 */
export interface IssueEmergencyAdminResetResult {
  resetUrl: string;
  expiresAt: string;
  resetTokenId: string;
  absoluteUrl?: string;
}

/**
 * `POST /auth/password-reset/issue-emergency-admin/:targetAdminId`.
 *
 * Supervisor-only kill-switch for the self-lockout gap. The target id
 * travels in the URL so the rate-limiter doesn't require parsing it.
 * Rate limit on the BE: 3 per hour. ⚠️ Здесь стояло «Per-IP» — снято
 * 10.09.2026 (второй круг ревью волны Р-116): у `@Throttle` настройки
 * «per-IP» нет вовсе, а трекер (`req.ip`) в композ-топологии равен адресу
 * контейнера nginx, поэтому ведро одно на маршрут и ОБЩЕЕ ДЛЯ ВСЕХ.
 * Практическое следствие для веба: 429 здесь может прийти от ЧУЖОГО
 * запроса к тому же маршруту, а не только от собственных повторов —
 * текст ошибки не должен обещать «вы исчерпали свой лимит». Разбор —
 * `.planning/block-2-object-tracker/DEBT.md`, долг #283. The mutation invalidates
 * `qk.users.all` because the target's `updatedAt` may shift on
 * subsequent token consumption (defensive — the issue itself doesn't
 * update the User row, but a follow-up reset will, and the supervisor
 * often re-checks the directory afterwards).
 *
 * Phase-2 retroactive devil-advocate (Bar 8): the body now MUST carry
 * `supervisorPassword`. This is a re-auth check — the BE argon2-verifies
 * it against the supervisor's stored hash and rejects with 401
 * `invalid_supervisor_password` on mismatch. The JWT alone is no longer
 * sufficient for this destructive op.
 */
export function useIssueEmergencyAdminResetMutation() {
  const qc = useQueryClient();
  return useMutation<
    IssueEmergencyAdminResetResult,
    unknown,
    { targetAdminId: string; supervisorPassword: string }
  >({
    mutationFn: ({ targetAdminId, supervisorPassword }) =>
      apiFetch<IssueEmergencyAdminResetResult>(
        `/auth/password-reset/issue-emergency-admin/${encodeURIComponent(targetAdminId)}`,
        { method: 'POST', body: { supervisorPassword } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users.all, refetchType: 'active' });
    },
  });
}
